/**
 * Sidekick AI car — the buddy unlocked by collecting two artifacts within
 * 10 seconds. Three behaviors:
 *   1. follow   — drive next to the player, keep up with their pace
 *   2. ram      — when a cop/FBI is within range, target it, ram it,
 *                 stun the cop on impact (no permanent kill — keeps the
 *                 sidekick from trivialising the race)
 *   3. jailbreak — when the player is in jail, drive to the cell, smash
 *                  the bars, and the player is freed
 *
 * Mode selection is implicit — every frame we pick the highest-priority
 * task: jailbreak > ram (if cop nearby) > follow.
 */
import { drawCar } from "./characters";

const RAM_RANGE = 200;
const FOLLOW_OFFSET = { x: -50, y: 30 };
const SIDEKICK_SPEED = 6.5;
const RAM_COOLDOWN = 1500;

export function updateSidekick(st) {
  const sk = st.sidekick;
  if (!sk || sk.hp <= 0) return;
  const p = st.player;
  const now = performance.now();

  // ---- Damage from cops/FBI -------------------------------------------------
  // Cop ramming sidekick → sidekick takes a chunk of HP; if HP drops to 0
  // it explodes and is removed (player loses the wingman for the rest of
  // this race; they have to earn another credit to redesign).
  for (let i = 0; i < st.enemies.length; i++) {
    if (st.destroyedEnemyIdx?.has(i)) continue;
    const e = st.enemies[i];
    if (e.disabled > now) continue;
    if (Math.hypot(sk.x - e.x, sk.y - e.y) < 24) {
      if ((sk.lastDamageAt || 0) + 700 < now) {
        sk.hp -= e.type === "fbi" ? 12 : 7;
        sk.lastDamageAt = now;
        sk.flashUntil = now + 200;
        if (sk.hp <= 0) {
          // Wreckage explosion
          if (st.explosions) {
            st.explosions.push({
              x: sk.x, y: sk.y, age: 0,
              accentColor: "#FFCC00", wreckage: true,
              particles: makeBreakoutSparks(sk.x, sk.y),
            });
          }
          st.sidekick = null;
          return;
        }
      }
    }
  }

  // ---- Damage from missiles -------------------------------------------------
  // Friendly-fire is allowed — missiles don't discriminate. Missiles do
  // chunky damage (one hit can take 30-50 HP) so accuracy matters.
  for (const m of st.missiles || []) {
    if ((sk.lastMissileHits || new Set()).has(m)) continue;
    if (Math.hypot(sk.x - m.x, sk.y - m.y) < 22) {
      sk.hp -= 35;
      sk.lastMissileHits = sk.lastMissileHits || new Set();
      sk.lastMissileHits.add(m);
      sk.flashUntil = now + 250;
      if (sk.hp <= 0) {
        if (st.explosions) {
          st.explosions.push({
            x: sk.x, y: sk.y, age: 0,
            accentColor: "#FFCC00", wreckage: true,
            particles: makeBreakoutSparks(sk.x, sk.y),
          });
        }
        st.sidekick = null;
        return;
      }
    }
  }

  // 1. Jail break has the highest priority. Player car has `jailedUntil`
  //    timestamp set when arrested; we drive to the police-station marker
  //    (top-right of the world: cx = WORLD_W - 80, cy = 80) and "smash"
  //    the bars by getting close enough.
  if (p.jailedUntil > now && !sk.jailBreakDone) {
    const targetX = 1280 - 80;
    const targetY = 80;
    sk.target = { x: targetX, y: targetY };
    sk.mode = "jailbreak";
    moveToward(sk, targetX, targetY, SIDEKICK_SPEED * 1.4);
    if (Math.hypot(sk.x - targetX, sk.y - targetY) < 50) {
      // Crash the cell — release the player.
      sk.jailBreakDone = true;
      p.jailedUntil = 0;
      // Teleport the player to a sane spot on the road and clear hits.
      p.x = targetX - 90;
      p.y = targetY + 100;
      p.hp = Math.max(p.hp, p.max_hp * 0.6);
      p.hitsTaken = 0;
      // Add a small explosion at the cell for VFX
      st.explosions.push({
        x: targetX, y: targetY + 10, age: 0,
        accentColor: "#FFCC00", wreckage: false,
        particles: makeBreakoutSparks(targetX, targetY + 10),
      });
    }
    advance(sk);
    return;
  }

  // 2. Ram a nearby live cop/FBI if any are in range.
  const target = pickRamTarget(st);
  if (target && now - sk.lastRam > RAM_COOLDOWN) {
    sk.mode = "ram";
    sk.target = { x: target.e.x, y: target.e.y };
    moveToward(sk, target.e.x, target.e.y, SIDEKICK_SPEED * 1.2);
    // Impact?
    if (Math.hypot(sk.x - target.e.x, sk.y - target.e.y) < 30) {
      sk.lastRam = now;
      // Knock the cop sideways + stun for 4s
      const dx = target.e.x - sk.x, dy = target.e.y - sk.y;
      const d = Math.hypot(dx, dy) || 1;
      const knock = 14;
      target.e.vx += (dx / d) * knock;
      target.e.vy += (dy / d) * knock;
      target.e.x += (dx / d) * 18;
      target.e.disabled = now + 4000;
    }
    advance(sk);
    return;
  }

  // 3. Default — follow the player.
  sk.mode = "follow";
  sk.target = null;
  // Aim for a slot just behind/right of the player; "behind" follows the
  // player's facing direction so we feel like a wingman, not a chase.
  const cos = Math.cos(p.angle), sin = Math.sin(p.angle);
  const fx = p.x + cos * FOLLOW_OFFSET.x - sin * FOLLOW_OFFSET.y;
  const fy = p.y + sin * FOLLOW_OFFSET.x + cos * FOLLOW_OFFSET.y;
  moveToward(sk, fx, fy, SIDEKICK_SPEED);
  advance(sk);
}

function moveToward(sk, tx, ty, maxSpeed) {
  const dx = tx - sk.x, dy = ty - sk.y;
  const d = Math.hypot(dx, dy) || 1;
  // Steer
  const ax = (dx / d) * 0.6;
  const ay = (dy / d) * 0.6;
  sk.vx += ax;
  sk.vy += ay;
  // Drag
  sk.vx *= 0.92;
  sk.vy *= 0.92;
  // Speed clamp
  const sp = Math.hypot(sk.vx, sk.vy);
  if (sp > maxSpeed) {
    sk.vx = (sk.vx / sp) * maxSpeed;
    sk.vy = (sk.vy / sp) * maxSpeed;
  }
}

function advance(sk) {
  sk.x += sk.vx;
  sk.y += sk.vy;
  // Smooth the heading — snapping to `atan2(vy, vx)` every frame makes the
  // sidekick spin wildly when it decelerates near its follow slot (vx+vy
  // oscillate around 0) and flips abruptly when the target changes. We only
  // update the facing when moving meaningfully, and LERP toward the new
  // angle so the rotation reads as smooth steering.
  const speed = Math.hypot(sk.vx, sk.vy);
  if (speed > 0.6) {
    const target = Math.atan2(sk.vy, sk.vx);
    const current = sk.angle ?? target;
    // Shortest-path angle diff wrapped to [-π, π]
    let diff = target - current;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    // Clamp per-frame rotation so large heading changes take a few frames.
    const maxStep = 0.18;  // ~10° per frame — fast but not snappy
    const step = Math.max(-maxStep, Math.min(maxStep, diff));
    sk.angle = current + step;
  }
}

function pickRamTarget(st) {
  let best = null;
  let bestD = RAM_RANGE;
  for (let i = 0; i < st.enemies.length; i++) {
    if (st.destroyedEnemyIdx?.has(i)) continue;
    const e = st.enemies[i];
    if (e.disabled > performance.now()) continue;
    const d = Math.hypot(st.sidekick.x - e.x, st.sidekick.y - e.y);
    if (d < bestD) { bestD = d; best = { i, e }; }
  }
  return best;
}

function makeBreakoutSparks(x, y) {
  const out = [];
  for (let i = 0; i < 18; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 2 + Math.random() * 4;
    out.push({
      x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
      r: 3 + Math.random() * 3, life: 30 + Math.random() * 15,
      type: "flame",
      hue: i % 2 === 0 ? "#FFCC00" : "#FFE39A",
    });
  }
  return out;
}

// ---- Render ----------------------------------------------------------------
export function drawSidekick(ctx, sk) {
  const d = sk.design;
  // Mode-aware glow ring under the car so the player can read its state.
  const glow = sk.mode === "jailbreak" ? "#FFCC00" : sk.mode === "ram" ? "#FF3B30" : "#34C759";
  ctx.save();
  ctx.fillStyle = `${glow}55`;
  ctx.beginPath();
  ctx.arc(sk.x, sk.y, 30 + Math.sin(performance.now() / 220) * 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = glow;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(sk.x, sk.y, 30, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // Base car
  drawCar(ctx, sk.x, sk.y, sk.angle, d.body_color, d.accent_color, 0.95, d.template);

  // Decals — drawn on top using the same 240×120 grid the designer used.
  // The car covers roughly the centered ~32×16 visible area so we map the
  // grid onto a 96×48 box centered on the car (0.4 px per cell at 0.95
  // scale). Sub-pixel cells naturally aggregate into recognisable shapes.
  if (d.decals && d.decals.length) {
    ctx.save();
    ctx.translate(sk.x, sk.y);
    ctx.rotate(sk.angle);
    const W = 96, H = 48;
    const cellW = W / 240, cellH = H / 120;
    for (const c of d.decals) {
      ctx.fillStyle = c.color;
      ctx.fillRect(-W / 2 + c.x * cellW, -H / 2 + c.y * cellH, cellW, cellH);
    }
    ctx.restore();
  }

  // Name banner above
  if (d.name) {
    ctx.save();
    ctx.font = "bold 9px 'Press Start 2P', monospace";
    ctx.textAlign = "center";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#000";
    ctx.fillStyle = glow;
    ctx.strokeText(d.name, sk.x, sk.y - 28);
    ctx.fillText(d.name, sk.x, sk.y - 28);
    ctx.restore();
  }

  // HP bar — shown only when below 100% so it doesn't clutter the screen
  // when the sidekick is fresh.
  if (sk.hp < (sk.max_hp || 100)) {
    const w = 32;
    const h = 4;
    const pct = Math.max(0, sk.hp / (sk.max_hp || 100));
    const bx = sk.x - w / 2;
    const by = sk.y - 38;
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    ctx.fillRect(bx - 1, by - 1, w + 2, h + 2);
    ctx.fillStyle = pct > 0.5 ? "#34C759" : pct > 0.25 ? "#FFCC00" : "#FF3B30";
    ctx.fillRect(bx, by, w * pct, h);
  }

  // Hit-flash — quick white overlay when just rammed/hit
  if ((sk.flashUntil || 0) > performance.now()) {
    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(sk.x, sk.y, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
